/**
 * Instagram message template shapes and limits.
 *
 * These are rich message formats, not pre-approved re-engagement templates:
 * they carry no Meta approval step and are subject to the same messaging
 * window as plain text. The limits below mirror
 * app/services/instagram/template_validator_service.rb, which is authoritative
 * -- this copy exists only so the builder can show errors before saving.
 */

export const IG_TEMPLATE_TYPES = {
  GENERIC: 'generic',
  BUTTON: 'button',
  QUICK_REPLY: 'quick_reply',
};

export const IG_LIMITS = {
  GENERIC_MAX_ELEMENTS: 10,
  GENERIC_MAX_TITLE: 80,
  GENERIC_MAX_SUBTITLE: 80,
  MAX_BUTTONS: 3,
  BUTTON_MAX_TEXT: 640,
  QUICK_REPLIES_MAX: 13,
  QUICK_REPLY_MAX_TITLE: 20,
};

export const IG_BUTTON_TYPES = ['web_url', 'postback'];

export const IG_QUICK_REPLY_CONTENT_TYPES = [
  'text',
  'user_phone_number',
  'user_email',
];

export const buildEmptyButton = () => ({
  type: 'web_url',
  title: '',
  url: '',
  payload: '',
});

export const buildEmptyElement = () => ({
  title: '',
  subtitle: '',
  image_url: '',
  buttons: [],
});

export const buildEmptyQuickReply = () => ({
  content_type: 'text',
  title: '',
  payload: '',
});

export const buildEmptyContent = templateType => {
  switch (templateType) {
    case IG_TEMPLATE_TYPES.BUTTON:
      return {
        template_type: templateType,
        text: '',
        buttons: [buildEmptyButton()],
      };
    case IG_TEMPLATE_TYPES.QUICK_REPLY:
      return {
        template_type: templateType,
        text: '',
        quick_replies: [buildEmptyQuickReply()],
      };
    default:
      return {
        template_type: IG_TEMPLATE_TYPES.GENERIC,
        elements: [buildEmptyElement()],
      };
  }
};

const tooLong = (value, limit) => Boolean(value) && value.length > limit;

const validateButton = (button, prefix) => {
  const errors = [];
  if (!button.title) errors.push(`${prefix}: title is required`);
  if (!IG_BUTTON_TYPES.includes(button.type)) {
    errors.push(`${prefix}: type must be one of ${IG_BUTTON_TYPES.join(', ')}`);
    return errors;
  }
  if (button.type === 'web_url' && !button.url) {
    errors.push(`${prefix}: url is required for web_url buttons`);
  }
  if (button.type === 'postback' && !button.payload) {
    errors.push(`${prefix}: payload is required for postback buttons`);
  }
  return errors;
};

const validateButtons = (buttons, prefix, required) => {
  const list = buttons || [];
  if (!list.length) {
    return required ? [`${prefix}: at least one button is required`] : [];
  }
  if (list.length > IG_LIMITS.MAX_BUTTONS) {
    return [`${prefix}: supports at most ${IG_LIMITS.MAX_BUTTONS} buttons`];
  }
  return list.flatMap((button, index) =>
    validateButton(button, `${prefix}: button ${index + 1}`)
  );
};

const validateGeneric = content => {
  const elements = content.elements || [];
  if (!elements.length) return ['elements must contain at least one card'];
  if (elements.length > IG_LIMITS.GENERIC_MAX_ELEMENTS) {
    return [
      `elements supports at most ${IG_LIMITS.GENERIC_MAX_ELEMENTS} cards`,
    ];
  }

  return elements.flatMap((element, index) => {
    const prefix = `card ${index + 1}`;
    const errors = [];
    if (!element.title) errors.push(`${prefix}: title is required`);
    if (tooLong(element.title, IG_LIMITS.GENERIC_MAX_TITLE)) {
      errors.push(
        `${prefix}: title exceeds ${IG_LIMITS.GENERIC_MAX_TITLE} characters`
      );
    }
    if (tooLong(element.subtitle, IG_LIMITS.GENERIC_MAX_SUBTITLE)) {
      errors.push(
        `${prefix}: subtitle exceeds ${IG_LIMITS.GENERIC_MAX_SUBTITLE} characters`
      );
    }
    return errors.concat(validateButtons(element.buttons, prefix, false));
  });
};

const validateButtonTemplate = content => {
  const errors = [];
  if (!content.text) errors.push('text is required');
  if (tooLong(content.text, IG_LIMITS.BUTTON_MAX_TEXT)) {
    errors.push(`text exceeds ${IG_LIMITS.BUTTON_MAX_TEXT} characters`);
  }
  return errors.concat(validateButtons(content.buttons, 'buttons', true));
};

const validateQuickReply = content => {
  const errors = [];
  if (!content.text) errors.push('text is required');

  const replies = content.quick_replies || [];
  if (!replies.length) {
    return errors.concat('quick_replies must contain at least one reply');
  }
  if (replies.length > IG_LIMITS.QUICK_REPLIES_MAX) {
    return errors.concat(
      `quick_replies supports at most ${IG_LIMITS.QUICK_REPLIES_MAX} replies`
    );
  }

  return errors.concat(
    replies.flatMap((reply, index) => {
      const prefix = `quick reply ${index + 1}`;
      const contentType = reply.content_type || 'text';
      if (!IG_QUICK_REPLY_CONTENT_TYPES.includes(contentType)) {
        return [
          `${prefix}: content_type must be one of ${IG_QUICK_REPLY_CONTENT_TYPES.join(', ')}`,
        ];
      }
      // Only text replies carry a label; phone/email are prefilled by Instagram.
      if (contentType !== 'text') return [];

      const replyErrors = [];
      if (!reply.title) replyErrors.push(`${prefix}: title is required`);
      if (tooLong(reply.title, IG_LIMITS.QUICK_REPLY_MAX_TITLE)) {
        replyErrors.push(
          `${prefix}: title exceeds ${IG_LIMITS.QUICK_REPLY_MAX_TITLE} characters`
        );
      }
      return replyErrors;
    })
  );
};

export const validateInstagramTemplate = (content = {}) => {
  switch (content.template_type) {
    case IG_TEMPLATE_TYPES.GENERIC:
      return validateGeneric(content);
    case IG_TEMPLATE_TYPES.BUTTON:
      return validateButtonTemplate(content);
    case IG_TEMPLATE_TYPES.QUICK_REPLY:
      return validateQuickReply(content);
    default:
      return ['template_type must be one of generic, button, quick_reply'];
  }
};

/** Placeholders ({{1}}, {{2}}, ...) an agent has to fill in before sending. */
export const instagramTemplateVariables = (content = {}) => {
  const texts = [
    content.text,
    ...(content.elements || []).flatMap(element => [
      element.title,
      element.subtitle,
      ...(element.buttons || []).map(button => button.title),
    ]),
    ...(content.buttons || []).map(button => button.title),
    ...(content.quick_replies || []).map(reply => reply.title),
  ];

  const found = texts
    .filter(Boolean)
    .flatMap(text => [...text.matchAll(/\{\{(\d+)\}\}/g)].map(m => m[1]));

  return [...new Set(found)].sort((a, b) => Number(a) - Number(b));
};
